import { pxToRem } from '@fluentsms/agentnet-web-components';
import {
  Box,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import {
  avlValidatedCode,
  AvlVerificationResponseType,
  validatedCode,
  VerificationResponseType,
} from 'api/verification/verification-api';
import Footer from 'core/footer/footer';
import { DocumentType, newWindow, WindowFeatures } from 'features/constants';
import { CPLErrorCodes } from 'features/cpl/cpl-list/constants';
import useAsync from 'hooks/useAsync';
import moment, { MomentInput } from 'moment';
import { useEffect, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import Logo from 'ui-kit/icons/AgentNetLogo';
import { AgentNetTextInput } from 'ui-kit/inputs';
import { openDocument } from 'utilities/utilities';
import VerificationBG from '../../common-assets/images/verification.jpg';
import { AvlVerificationStatusType, VerificationStatusType } from './types';
const headerHeight = 64;
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    imageContainer: {
      position: 'relative',
      width: '100%',
      backgroundColor: theme.palette.common.white,
      display: 'flex',
      alignItems: 'center',
      borderRadius: '6px 6px 0 0',
      justifyContent: 'center',
      overflow: 'hidden',
    },
    headerBar: {
      background: theme.palette.brand.legacy,
      height: headerHeight,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      padding: '0 25px',
    },
    topAlign: {
      alignSelf: 'flex-start',
      background: theme.palette.common.white,
      justifyContent: 'center',
      height: `calc(100% - ${headerHeight}px)`,
      overflow: 'auto',
    },
    contentContainer: {
      height: '100%',
      paddingTop: theme.spacing(6),
    },
    paper: {
      maxWidth: 800,
    },
    ml0: {
      marginLeft: '0',
    },
    image: { minHeight: '100%', width: '100%', position: 'absolute', top: 0 },
    imageOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      backgroundColor: `${theme.palette.common.black}8C`,
      zIndex: 1,
    },
    imageText: {
      zIndex: 2,
      padding: theme.spacing(6, 0),
      color: theme.palette.common.white,
      textAlign: 'center',
      maxWidth: '40ch',
      fontWeight: 500,
      textTransform: 'none',
    },
    textContainer: {
      padding: theme.spacing(2, 3),
    },
    tableCell: {
      borderWidth: 0,
      height: 'auto !important',
      padding: `${theme.spacing(0, 0, 0.5)} !important`,
    },
  }),
);

const Verification = (): JSX.Element => {
  const classes = useStyles();
  const searchParams = new URLSearchParams(location.search);
  const [validationCode, setValidationCode] = useState(searchParams.get('DigitalSignature') ?? '');
  const [isCpl, setCpl] = useState<boolean>(validationCode && validationCode.indexOf('AVL') > -1 ? false : true);
  const [errMsg, setErrMsg] = useState('');

  const [displayWarning, setDisplayWarning] = useState<boolean>(false);
  const [warningMsg, setWarningMsg] = useState<string>('');

  const [rows, setRows] = useState<{ title: string; value: string }[] | null>(null);
  const [pdfData, setPdfData] = useState('');
  const loadVerificationStatus = () => (isCpl ? validatedCode(validationCode) : avlValidatedCode(validationCode));
  const { execute, value, status, errors, errorDetails } = useAsync<
    VerificationResponseType | AvlVerificationResponseType
  >(loadVerificationStatus, false);
  const [shouldAutoSearch, setShouldAutoSearch] = useState<boolean>(
    searchParams.get('DigitalSignature') ? true : false,
  );

  useEffect(() => {
    if (value?.result) {
      let tableRows;
      if (isCpl) {
        const result = value.result as VerificationStatusType;
        tableRows = [
          { title: 'File #', value: result?.fileNumber ?? '' },
          { title: 'Letter Type', value: result?.letterType ?? '' },
          { title: 'Created Date', value: moment(result?.updatedDate).format('L') || '' },
          { title: 'Office Originated in', value: result?.officeName ?? '' },
          { title: 'Created By', value: result?.updatedBy ?? '' },
        ];

        setPdfData(result?.pdfData);
      } else {
        const result = value.result as AvlVerificationStatusType;
        tableRows = [
          { title: 'Letter Type', value: result?.LetterType ?? '' },
          { title: 'Agent', value: result?.AgentName ?? '' },
          { title: 'Address', value: `${result?.AgentAddressLine1}, ${result?.AgentAddressLine2}` },
          { title: 'Created Date', value: moment(result?.CreatedDate).format('L') || '' },
          { title: 'Office Originated in', value: result?.OfficeName ?? '' },
          { title: 'Office Id', value: result?.OfficeId?.toString() ?? '' },
          { title: 'Created By', value: result?.CreatedBy ?? '' },
        ];

        setPdfData(result?.Document?.Content);
      }
      setRows(tableRows);
    }
  }, [value]);

  useEffect(() => {
    if (errorDetails && errorDetails?.errorCode == CPLErrorCodes.badRequest) {
      setDisplayWarning(true);
      setWarningMsg('Please enter a valid Digital Signature.');
    } else if (isCpl && errorDetails && errorDetails?.errorCode == CPLErrorCodes.forbidden) {
      const date = errorDetails?.data?.data?.updatedDate ?? null;
      const name = errorDetails?.data?.data?.updatedBy ?? '';

      const errorText = `The CPL you are searching for has been voided on ${moment(date as MomentInput).format(
        'MM/DD/YYYY',
      )} by ${name}; therefore the CPL cannot be verified.`;
      setErrMsg(errorText);
    } else {
      if (errors && errors.length > 0) {
        setErrMsg(errors.join(', '));
      }
    }
  }, [errorDetails?.errorCode]);

  useEffect(() => {
    if (searchParams.get('DigitalSignature') && shouldAutoSearch) {
      setShouldAutoSearch(false);
      onValidationSearch();
    }
  }, []);

  const onValidationSearch = () => {
    if (validationCode) {
      setRows(null);
      setDisplayWarning(false);
      setWarningMsg('');
      setErrMsg('');
      execute().then();
    } else {
      setDisplayWarning(true);
      setWarningMsg('Digital Signature is required.');
    }
  };

  return (
    <Grid container className={classes.topAlign}>
      <div className={classes.headerBar}>
        <Logo />
      </div>

      <div className={classes.contentContainer}>
        <Paper className={classes.paper} elevation={2}>
          <div className={classes.imageContainer}>
            <img className={classes.image} src={VerificationBG} alt="verification" />
            <div className={classes.imageOverlay}></div>
            <Typography variant="h1" className={classes.imageText}>
              Closing Protection Letters and Agency Verification Made Simple and Convenient
            </Typography>
          </div>
          <Box p={3}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={5}>
                <AgentNetTextInput
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onValidationSearch();
                    }
                  }}
                  variant="outlined"
                  fullWidth
                  label={'Digital Signature'}
                  value={validationCode}
                  onChange={(e) => {
                    e.target.value && e.target.value.indexOf('AVL') > -1 ? setCpl(false) : setCpl(true);
                    setValidationCode(e.target.value);
                    setDisplayWarning(false);
                    setErrMsg('');
                  }}
                  error={displayWarning}
                  style={{ marginTop: displayWarning ? pxToRem(22) : 0 }}
                  helperText={displayWarning && warningMsg}
                  FormHelperTextProps={{ classes: { root: classes.ml0 } }}
                />
              </Grid>
              <Grid item xs={1}>
                <AgentNetButton
                  color="primary"
                  size="small"
                  className="button"
                  variant="contained"
                  loading={status === 'pending' ? true : undefined}
                  disabled={status === 'pending' ? true : undefined}
                  onClick={() => {
                    onValidationSearch();
                  }}
                >
                  Search
                </AgentNetButton>
              </Grid>
            </Grid>
            {rows && (
              <Grid item xs={12}>
                <Box pt={3} pb={2}>
                  <Typography variant="h3">
                    {isCpl ? 'Additional CPL Information' : 'Additional AVL Information'}
                  </Typography>
                </Box>
                <TableContainer>
                  <Table aria-label="Additional Information">
                    <TableBody>
                      {rows &&
                        rows.map((row) => (
                          <TableRow key={row.title}>
                            <TableCell component="th" scope="row" width={180} className={classes.tableCell}>
                              <Typography variant="body1">{row.title}:</Typography>
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              <Typography variant="body1">{row.value}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box pt={2}>
                  <AgentNetButton
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => {
                      if (pdfData) openDocument(pdfData, DocumentType, newWindow, WindowFeatures);
                    }}
                  >
                    Download PDF
                  </AgentNetButton>
                </Box>
              </Grid>
            )}
            {errMsg && (
              <Box pt={2}>
                <Alert severity="error" variant="outlined">
                  {errMsg}
                </Alert>
              </Box>
            )}
            <Box pt={2}>
              <Typography variant="body2">
                IMPORTANT: First American Title Insurance Company, provides this Closing Protection Letter and Agency
                Verification Letter verification as a service to the lender community. Any abuse of this service may
                result in your exclusion from this service or other penalties as appropriate.
              </Typography>
              <br />
              <Typography variant="body2">
                NOTE: Closing Letters are generated in a PDF format. You will need a PDF Reader to view Closing Letters.
                You can get a free one at{' '}
                <Link
                  href="http://www.adobe.com/products/acrobat/readstep2.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Adobe Acrobat
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </div>
      <Footer />
    </Grid>
  );
};
export default Verification;
